import config from '@/common/config'
import dayjs from '@/services/date'
import { ClientType } from '@/shared/constants/client/ClientType'
import { DurationUnits } from '@/shared/constants/date/DurationUnits'
import hasPassedTimeToday from '@/utils/has-passed-time-today'
import parseTimeStringToObject from '@/utils/parse-time-string-to-object'
import Country from '@/shared/constants/location/country'

/**
 * Australian client types exempt from the standard same-day cut-off, as requested by Darragh Kelly/Craig Griffiths.
 * @see {@link https://dev.azure.com/anzukeducation/Staffroom%20NEXT User Story #2298}
 */
const SAME_DAY_CUT_OFF_EXEMPT_AUS_CLIENT_TYPES = [
  ClientType.EARLY_CHILDHOOD,
  ClientType.OSHC,
  ClientType.KINDERGARTEN,
]

export const getStandardSameDayCutOff = () => {
  try {
    return parseTimeStringToObject(config.get('sameDayBookingCutOffTime'))
  } catch (error) {
    console.warn('Failed to parse same day cut off time. Defaulting to 8:00 am')
    return { hours: 8, minutes: 0 }
  }
}

const getUSSameDayCutOff = () => {
  const {
    bufferWindowStartTime,
    cutOffTime,
    latestBufferedStartTime,
    startTimeBufferMinutes,
  } = config.get('usSameDayBookingCutOff')

  return {
    bufferWindowStartTime: parseTimeStringToObject(bufferWindowStartTime),
    cutOffTime: parseTimeStringToObject(cutOffTime),
    latestBufferedStartTime: parseTimeStringToObject(latestBufferedStartTime),
    startTimeBufferMinutes,
  }
}

const hasPassedSameDayCutOff = (timezone, isUSLocation) => {
  const cutOffTime = isUSLocation
    ? getUSSameDayCutOff().cutOffTime
    : getStandardSameDayCutOff()

  return hasPassedTimeToday(cutOffTime, timezone)
}

export function getUSBookingStartWindow(timezone) {
  const {
    bufferWindowStartTime,
    cutOffTime,
    latestBufferedStartTime,
    startTimeBufferMinutes,
  } = getUSSameDayCutOff()

  const now = dayjs().tz(timezone)
  const startOfToday = now.startOf(DurationUnits.DAY)

  // Before the buffer window starts, bookings can be made immediately
  if (now.isBefore(startOfToday.add(bufferWindowStartTime))) {
    return { earliestStart: now, latestSameDayStart: null }
  }

  // Before the cut-off time bookings must be at least the buffer window duration ahead
  if (now.isBefore(startOfToday.add(cutOffTime))) {
    return {
      earliestStart: now.add(startTimeBufferMinutes, DurationUnits.MINUTE),
      latestSameDayStart: startOfToday.add(latestBufferedStartTime),
    }
  }

  // After the cut-off time, the earliest start is the next day
  return {
    earliestStart: startOfToday.add(1, DurationUnits.DAY),
    latestSameDayStart: null,
  }
}

export const hasPassedSameDayCutOffForLocation = (location, isUSLocation) => {
  const isExemptFromSameDayCutOff =
    location.country === Country.Australia &&
    SAME_DAY_CUT_OFF_EXEMPT_AUS_CLIENT_TYPES.includes(location.clientTypeInt)

  if (isExemptFromSameDayCutOff) return false

  return hasPassedSameDayCutOff(location.timeZone, isUSLocation)
}

export const hasAtLeastOneLocationNotPassedSameDayCutOff = (
  locations,
  isUSLocation
) =>
  locations.some(
    (location) => !hasPassedSameDayCutOffForLocation(location, isUSLocation)
  )
